import { useUpdateScreenChatContext } from '$tablet-app/src/app/chat/hooks';
import LoadingSpinner from '$tablet-app/src/app/components/LoadingSpinner';
import { ScreenBackButton } from '$tablet-app/src/app/components/ScreenBackButton';
import { ScreenHeader } from '$tablet-app/src/app/components/ScreenHeader';
import { ScreenTitle } from '$tablet-app/src/app/components/ScreenTitle';
import { DeveloperScreenInfo } from '$tablet-app/src/app/developer/components/DeveloperScreenInfo/DeveloperScreenInfo';
import { RootStackScreenProps } from '$tablet-app/src/app/navigation/RootStackNavigator';
import { colors, spacing } from '@akin-home/theme';
import { showErrorToast, showSuccessToast } from '@akin-home/toast/native';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React from 'react';
import { FlatList, StyleSheet, Text, View } from 'react-native';
import styled from 'styled-components/native';
import { KnownRobotCard } from '../../components/KnownRobotsBar/KnownRobotCard';
import { useRobotStateStore } from '../../stores/useRobotStateStore';
import { useTriggerRobotFlow } from '../hooks/mutations';
import { useGetRobotTutorials } from '../hooks/queries';
import { RobotTutorial } from '../types';
import { RobotTutorialButton } from './RobotTutorialButton';

export const RobotTutorialsScreen = (
  props: RootStackScreenProps<'RobotTutorials'>
) => {
  const {
    navigation: { goBack },
    route: {
      params: { robotId },
    },
  } = props;

  useUpdateScreenChatContext();
  // useEffect(() => {
  //   sendBackgroundMessage(intent_labels.open_create_checklist_screen);
  // }, []);

  const robot = useRobotStateStore((state) => state.robotDevices[robotId]);

  if (!robot) {
    showErrorToast({
      text1: 'Robot not found.',
    });
    goBack();
  }

  const { data: tutorials, isLoading } = useGetRobotTutorials(robot);
  const triggerFlowMut = useTriggerRobotFlow(robot, {
    onSuccess: () => {
      showSuccessToast({
        text1: 'Tutorial started',
        text2: 'Please look at your robot',
      });
    },
    onError: () => {
      showErrorToast({
        text1: 'Starting the tutorial failed',
        text2: 'Please try again',
      });
    },
  });

  const onTutorialPress = async (tutorial: RobotTutorial) => {
    if (!robot || !tutorial?.id) {
      return;
    }

    try {
      await triggerFlowMut.mutateAsync(tutorial.id);
    } catch (err) {
      console.error('Error in executing tutorial flow', err);
    }
  };

  const isRobotConnected =
    robot?.websocketConnection?.connectionStatus === 'connected' ?? false;

  return (
    <>
      <Screen>
        <Header>
          <ScreenBackButton onPress={goBack} />
          <ScreenTitle>Robot Tutorials</ScreenTitle>

          <View style={styles.robotCardContainer}>
            {robot ? (
              <KnownRobotCard robot={robot} />
            ) : (
              <>
                <FontAwesomeIcon
                  icon={'warning'}
                  size={30}
                  style={{ height: '100%', width: '100%' }}
                  color={colors.yellow[700]}
                />
                <Text
                  style={{
                    textAlign: 'center',
                    textAlignVertical: 'center',
                    fontSize: 22,
                    color: colors.yellow[700],
                  }}
                >
                  Robot Not Available
                </Text>
              </>
            )}
          </View>
        </Header>

        <View style={styles.screen}>
          <FlatList
            style={styles.flatlist}
            data={tutorials}
            renderItem={({ item, index }) => (
              <RobotTutorialButton
                key={`${item.id}_${index}`}
                tutorial={item}
                disabled={!isRobotConnected}
                onPress={() => onTutorialPress(item)}
              />
            )}
          />

          {isLoading || triggerFlowMut.isLoading ? (
            <LoadingSpinner style={styles.loadingOverlay} size={100} />
          ) : null}
        </View>
      </Screen>
      <DeveloperScreenInfo />
    </>
  );
};

const Screen = styled.View`
  flex: 1;
`;

const Header = styled(ScreenHeader)`
  justify-content: flex-start;
  gap: 20px;
  padding-bottom: 10px;
`;

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: 'white',
    margin: spacing[5],
    marginTop: 0,
    elevation: 3,
    borderRadius: 10,
  },
  robotCardContainer: {
    marginLeft: 'auto',
    flexDirection: 'row',
    gap: spacing[2],
  },
  flatlist: {
    flex: 1,
    padding: spacing[3],
  },
  loadingOverlay: {
    backgroundColor: 'white',
    opacity: 0.5,
    width: '100%',
    height: '100%',
    position: 'absolute',
    paddingBottom: 2 * spacing[8],
  },
});
